// Period-flavoured AI operators for contract drilling (DESIGN §17).
// Pure data + a name generator. No randomness of its own: callers pass `rand()` (0..1), e.g.
// `() => nextFloat(state)` from src/core/rng.js, so the sim stays deterministic.

// Surnames by region (period-plausible for the people who actually ran those fields).
export const SURNAMES = {
  pennsylvania: ['Brewer', 'Watson', 'Clapp', 'Funk', 'Phillips', 'Rouse', 'Mitchell', 'Densmore', 'Noble', 'Delamater',
    'Hyde', 'Egbert', 'Abbott', 'Harley', 'Vandergrift', 'McKinney', 'Benninghoff', 'Steele', 'Story', 'Bissell',
    'Eveleth', 'Frew', 'Lockhart', 'Emery', 'Galey', 'Guffey', 'Haldeman', 'Hasson', 'Wadsworth', 'Ferris'],
  ontario: ['Williams', 'Shaw', 'Fairbank', 'Tripp', 'Englehart', 'Kittridge', 'McGarvey', 'Vaughan', 'Duncan', 'Ross'],
  ohio: ['Lima', 'Faurot', 'Carter', 'Ohl', 'Mowrey', 'Bowers', 'Hover', 'Holmes', 'Treadwell', 'Gamble'],
  romania: ['Lupescu', 'Mehedinţeanu', 'Câmpineanu', 'Moţoc', 'Ghica', 'Drăgulescu', 'Siliştea', 'Marghiloman', 'Bădescu', 'Popovici'],
  galicia: ['Łukasiewicz', 'Klobassa', 'Szczepanowski', 'MacGarvey', 'Bergheim', 'Gartenberg', 'Zieliński', 'Trzecieski', 'Gołąbek', 'Perkins'],
  baku: ['Mantashev', 'Tagiyev', 'Mukhtarov', 'Lianozov', 'Aramyants', 'Kokorev', 'Nagiyev', 'Asadullayev', 'Shibayev', 'Gukasov'],
  sumatra: ['Zijlker', 'Kessler', 'de Gelder', 'Deterding', 'van Vollenhoven', 'Stoop', 'Loudon', 'Fentener', 'Meijer', 'Cremer'],
  texas: ['Higgins', 'Lucas', 'Hamill', 'Sharp', 'Cullinan', 'Hogg', 'Joiner', 'Hunt', 'Richardson', 'Murchison', 'Cullen', 'Kilgore'],
  oklahoma: ['Skelly', 'Sinclair', 'Marland', 'Phillips', 'Getty', 'Chapman', 'Barnsdall', 'Slick', 'Galbreath', 'Mullendore'],
  default: ['Smith', 'Carter', 'Hale', 'Monroe', 'Whitaker', 'Pierce', 'Lowell', 'Harmon', 'Bishop', 'Reed'],
};

// Place names used in company names.
export const PLACES = {
  pennsylvania: ['Venango', 'Allegheny', 'Cherry Run', 'Tarr Farm', 'Oil Creek', 'Titusville', 'Franklin', 'Pithole',
    'Rouseville', 'Tidioute', 'Petroleum Centre', 'Pleasantville', 'McClintock', 'Bradford', 'Warren', 'Seneca', 'Keystone'],
  ontario: ['Enniskillen', 'Oil Springs', 'Petrolia', 'Lambton', 'Canada West', 'Bothwell'],
  ohio: ['Lima', 'Trenton', 'Findlay', 'Buckeye', 'Auglaize', 'Black Swamp'],
  romania: ['Prahova', 'Câmpina', 'Băicoi', 'Wallachian', 'Danubian', 'Moreni'],
  galicia: ['Boryslav', 'Galician', 'Carpathian', 'Drohobych', 'Schodnica', 'Lemberg'],
  baku: ['Caspian', 'Balakhany', 'Sabunchi', 'Bibi-Heybat', 'Absheron', 'Surakhany'],
  sumatra: ['Langkat', 'Sumatra', 'Telaga', 'Deli', 'Indies', 'Pangkalan'],
  texas: ['Gladys City', 'Sour Lake', 'Humble', 'Beaumont', 'Gulf Coast', 'Spindletop', 'Lone Star', 'Rusk County'],
  oklahoma: ['Glenn Pool', 'Cushing', 'Osage', 'Indian Territory', 'Red Fork', 'Sooner'],
  default: ['Continental', 'Union', 'Eagle', 'Liberty', 'Pioneer', 'Atlas', 'Meridian', 'Frontier'],
};

// Name patterns by era. {A}/{B} = surnames, {P} = place.
export const PATTERNS = [
  { from: 1859, to: 1900, list: ['{A} & {B}', '{A} Bros.', '{A}, {B} & Co.', '{P} Oil Co.', '{P} Petroleum Co.', '{A} & Sons', '{A} Oil Co.', '{P} Rock Oil Co.', '{A} & {B} Oil Works'] },
  { from: 1901, to: 1945, list: ['{P} Oil Co.', '{A} Drilling Co.', '{A}-{B} Oil Corp.', '{P} Crude Oil Co.', '{A} Petroleum Corp.', '{A} & {B} Producers', '{P} Oil & Refining Co.'] },
  { from: 1946, to: 1999, list: ['{P} Oil & Gas Co.', '{A} Exploration Inc.', '{P} Energy Corp.', '{A}-{B} Resources', '{A} Petroleum Inc.', '{P} Offshore Co.'] },
  { from: 2000, to: 2059, list: ['{P} Energy Partners LP', '{A} Resources Inc.', '{P} Upstream LLC', '{A} {P} Energy', '{P} Basin Holdings'] },
  { from: 2060, to: 9999, list: ['{P} Volatiles Consortium', '{A} Off-World Extraction', '{P} Cryo-Resources', '{A}-{B} Orbital Holdings'] },
];

// The big rivals (historical), appearing in their time and place. `regions: null` = anywhere.
export const MAJORS = [
  { id: 'standard', name: 'Standard Oil Co.', short: 'Standard', from: 1870, to: 1911, regions: ['pennsylvania', 'ohio'] },
  { id: 'branobel', name: 'Nobel Brothers Petroleum Co.', short: 'Branobel', from: 1879, to: 1920, regions: ['baku'] },
  { id: 'bnito', name: 'Caspian & Black Sea Oil Co.', short: 'BNITO', from: 1883, to: 1912, regions: ['baku'] },
  { id: 'royal_dutch', name: 'Royal Dutch Petroleum Co.', short: 'Royal Dutch', from: 1890, to: 9999, regions: ['sumatra'] },
  { id: 'galician', name: 'Galician Carpathian Petroleum Co.', short: 'Galician Carpathian', from: 1895, to: 1939, regions: ['galicia'] },
  { id: 'romano_americana', name: 'Româno-Americană', short: 'Româno-Americană', from: 1904, to: 1948, regions: ['romania'] },
  { id: 'texas_co', name: 'The Texas Company', short: 'Texaco', from: 1902, to: 9999, regions: null },
  { id: 'gulf', name: 'Gulf Oil Corporation', short: 'Gulf', from: 1907, to: 1984, regions: null },
  { id: 'humble', name: 'Humble Oil & Refining Co.', short: 'Humble', from: 1917, to: 1972, regions: null },
];

// Pseudonymous "nickname" short forms for well names: "Clapp Bros. No. 3".
export function shortName(name) {
  return String(name).replace(/,? (Co\.|Corp\.|Inc\.|LLC|LP|Corporation|Company)$/,'').replace(/^The /, '').trim();
}

const pickR = (rand, arr) => arr[Math.min(arr.length - 1, Math.floor(rand() * arr.length))];

export function patternsFor(year) {
  return (PATTERNS.find((p) => year >= p.from && year <= p.to) ?? PATTERNS[0]).list;
}

export function majorsFor(year, regionId) {
  return MAJORS.filter((m) => year >= m.from && year <= m.to && (!m.regions || m.regions.includes(regionId)));
}

// Build one operator name. Returns { name, short, kind: 'independent' }.
export function generateOperatorName(rand, { year = 1860, regionId = 'pennsylvania' } = {}) {
  const sn = SURNAMES[regionId] ?? SURNAMES.default;
  const pl = PLACES[regionId] ?? PLACES.default;
  const pat = pickR(rand, patternsFor(year));
  const a = pickR(rand, sn);
  let b = pickR(rand, sn);
  if (b === a) b = sn[(sn.indexOf(a) + 1) % sn.length];
  const name = pat.replace('{A}', a).replace('{B}', b).replace('{P}', pickR(rand, pl));
  return { name, short: shortName(name), kind: 'independent' };
}

// Satisfaction → how keen an operator is to call you again (weight for offer generation).
export const operatorWeight = (op) => 10 + Math.max(0, op?.satisfaction ?? 50);

// Flavour for the board header, by era number.
export const BOARD_FLAVOR = {
  1: 'Posted at the Titusville exchange: wells wanted, rigs and crews by the day, the foot, or the job.',
  2: 'Wanted on the Gulf Coast and in the Territory: rotary outfits and crews who know mud.',
  3: 'Concession holders and independents seeking contractors. Offshore charters for the bold.',
  4: 'Day-rate market: the majors are hiring every rig that floats — and dropping them just as fast.',
  5: 'Pad programs and deepwater campaigns. Operators want speed, safety records and no surprises.',
  6: 'Autonomous rig pools and Arctic charters. Clients audit your safety record in real time.',
  7: 'Off-world drilling tenders. Payment in Solar Credits on delivery of core.',
};
